import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, X, Phone, Mail, User } from 'lucide-react';
import {
  ShadcnDialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  Button,
  Input,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  Toast,
  ToastType,
  AutocompleteInput,
  AutocompleteOption,
} from '@/components/ui';
import { apiService } from '@/services/api';
import { Lead } from '@/types';
import { WhatsAppTemplate } from '../types';

interface NewConversationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConversationCreated: (phoneNumber: string, leadId: string) => void;
}

export const NewConversationDialog: React.FC<NewConversationDialogProps> = ({
  open,
  onOpenChange,
  onConversationCreated,
}) => {
  const { t } = useTranslation();
  const [toast, setToast] = useState<{ message: string; type: ToastType } | null>(null);
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState<'search' | 'create'>('search');

  // Lead search state
  const [leadOptions, setLeadOptions] = useState<AutocompleteOption[]>([]);
  const [leadMap, setLeadMap] = useState<Map<string, Lead>>(new Map());
  const [selectedLead, setSelectedLead] = useState<Lead | null>(null);
  const [selectedLeadId, setSelectedLeadId] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState('');
  const [loadingLeads, setLoadingLeads] = useState(false);

  // Create lead form state
  const [newLead, setNewLead] = useState({
    name: '',
    phone: '',
    email: '',
    source: 'WhatsApp',
  });

  // Template state
  const [templates, setTemplates] = useState<WhatsAppTemplate[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<WhatsAppTemplate | null>(null);
  const [templateParameters, setTemplateParameters] = useState<Record<number, string>>({});
  const [loadingTemplates, setLoadingTemplates] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (searchQuery.length < 2) {
      setLeadOptions([]);
      return;
    }

    const delayDebounce = setTimeout(async () => {
      setLoadingLeads(true);
      try {
        const response = await apiService.getLeads({ search: searchQuery, limit: 10, isWhatsApp: true });
        const newLeadMap = new Map<string, Lead>();
        const options: AutocompleteOption[] = response.leads.map((lead: Lead) => {
          newLeadMap.set(lead.id, lead);
          const primaryPhone = lead.phones?.find(p => p.isPrimary) || lead.phones?.[0];
          const phoneDisplay = primaryPhone
            ? `(${primaryPhone.ddd}) ${primaryPhone.phone}`
            : '';

          return {
            id: lead.id,
            label: lead.name,
            meta: phoneDisplay,
          };
        });
        setLeadMap(newLeadMap);
        setLeadOptions(options);
      } catch (error) {
        console.error('Error loading leads:', error);
      } finally {
        setLoadingLeads(false);
      }
    }, 300);

    return () => clearTimeout(delayDebounce);
  }, [searchQuery, open]);

  useEffect(() => {
    if (!open || !selectedLead) return;

    const loadTemplates = async () => {
      setLoadingTemplates(true);
      try {
        const response = await apiService.getWhatsAppTemplates();
        // Suporta tanto múltiplas contas quanto conta única
        let allTemplates: WhatsAppTemplate[] = [];
        if (response.accounts) {
          allTemplates = response.accounts.flatMap(acc => acc.templates);
        } else if (response.templates) {
          allTemplates = response.templates;
        }
        const approvedTemplates = allTemplates.filter((t: WhatsAppTemplate) => t.status === 'APPROVED' && t.isActive);
        setTemplates(approvedTemplates);
      } catch (error) {
        console.error('Error loading templates:', error);
        setToast({
          message: t('conversations.errors.loadTemplatesError'),
          type: 'error',
        });
      } finally {
        setLoadingTemplates(false);
      }
    };

    loadTemplates();
  }, [open, selectedLead, t]);

  const getLeadWhatsAppPhone = (lead: Lead): string | null => {
    if (lead.phones && lead.phones.length > 0) {
      const primaryWhatsApp = lead.phones.find((p) => p.isWhatsApp && p.isPrimary);
      if (primaryWhatsApp) {
        const ddi = primaryWhatsApp.ddi?.replace('+', '') || '55';
        return `${ddi}${primaryWhatsApp.ddd || ''}${primaryWhatsApp.phone}`;
      }

      const anyWhatsApp = lead.phones.find((p) => p.isWhatsApp);
      if (anyWhatsApp) {
        const ddi = anyWhatsApp.ddi?.replace('+', '') || '55';
        return `${ddi}${anyWhatsApp.ddd || ''}${anyWhatsApp.phone}`;
      }

      const primaryPhone = lead.phones.find((p) => p.isPrimary);
      if (primaryPhone) {
        const ddi = primaryPhone.ddi?.replace('+', '') || '55';
        return `${ddi}${primaryPhone.ddd || ''}${primaryPhone.phone}`;
      }

      const firstPhone = lead.phones[0];
      const ddi = firstPhone.ddi?.replace('+', '') || '55';
      return `${ddi}${firstPhone.ddd || ''}${firstPhone.phone}`;
    }

    return lead.phone;
  };

  const extractTemplateParameters = (template: WhatsAppTemplate): number => {
    const bodyComponent = template.components.find((c) => c.type === 'BODY');
    if (!bodyComponent || !bodyComponent.text) return 0;

    const matches = bodyComponent.text.match(/\{\{(\d+)\}\}/g);
    return matches ? matches.length : 0;
  };

  const handleLeadSelect = (option: AutocompleteOption) => {
    const lead = leadMap.get(option.id);
    if (lead) {
      setSelectedLead(lead);
      setSelectedLeadId(option.id);
      setMode('search');
    }
  };

  const handleTemplateSelect = (templateId: string) => {
    const template = templates.find((t) => t.id === templateId);
    if (template) {
      setSelectedTemplate(template);
      const paramCount = extractTemplateParameters(template);
      const newParams: Record<number, string> = {};
      for (let i = 1; i <= paramCount; i++) {
        newParams[i] = '';
      }
      setTemplateParameters(newParams);
    }
  };

  const handleCreateLead = async () => {
    // Validation
    if (!newLead.name.trim()) {
      setToast({ message: t('conversations.newConversation.nameRequired'), type: 'error' });
      return;
    }
    if (!newLead.phone.trim()) {
      setToast({ message: t('conversations.newConversation.phoneRequired'), type: 'error' });
      return;
    }

    setLoading(true);
    try {
      const created = await apiService.createLead({
        name: newLead.name.trim(),
        phone: newLead.phone.trim(),
        email: newLead.email.trim() || null,
        source: newLead.source,
        status: 'new',
      });

      setSelectedLead(created);
      setMode('search');
      setToast({ message: t('conversations.newConversation.leadCreated'), type: 'success' });
    } catch (error: any) {
      console.error('Error creating lead:', error);
      setToast({
        message: error.message || t('conversations.newConversation.createLeadError'),
        type: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleStartConversation = async () => {
    if (!selectedLead) {
      setToast({ message: t('conversations.newConversation.selectLeadFirst'), type: 'error' });
      return;
    }

    const phoneNumber = getLeadWhatsAppPhone(selectedLead);
    if (!phoneNumber) {
      setToast({ message: t('conversations.newConversation.leadNoPhone'), type: 'error' });
      return;
    }

    if (!selectedTemplate) {
      setToast({ message: t('conversations.newConversation.selectTemplateFirst'), type: 'error' });
      return;
    }

    const paramCount = extractTemplateParameters(selectedTemplate);
    const missingParams = [];
    for (let i = 1; i <= paramCount; i++) {
      if (!templateParameters[i] || !templateParameters[i].trim()) {
        missingParams.push(i);
      }
    }

    if (missingParams.length > 0) {
      setToast({
        message: t('conversations.newConversation.fillAllParameters'),
        type: 'error',
      });
      return;
    }

    setLoading(true);
    try {
      const parameters = Object.keys(templateParameters)
        .sort((a, b) => parseInt(a) - parseInt(b))
        .map((key) => templateParameters[parseInt(key)]);

      await apiService.sendWhatsAppTemplateMessage({
        to: phoneNumber,
        templateName: selectedTemplate.name,
        languageCode: selectedTemplate.language,
        parameters: parameters.length > 0 ? parameters : undefined,
        leadId: selectedLead.id,
      });

      setToast({
        message: t('conversations.newConversation.templateSent'),
        type: 'success',
      });

      onConversationCreated(phoneNumber, selectedLead.id);
      handleClose();
    } catch (error: any) {
      console.error('Error sending template:', error);
      setToast({
        message: error.message || t('conversations.errors.sendError'),
        type: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSelectedLead(null);
    setSelectedLeadId('');
    setSearchQuery('');
    setLeadOptions([]);
    setLeadMap(new Map());
    setNewLead({ name: '', phone: '', email: '', source: 'WhatsApp' });
    setMode('search');
    setTemplates([]);
    setSelectedTemplate(null);
    setTemplateParameters({});
    onOpenChange(false);
  };

  return (
    <>
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
      <ShadcnDialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>{t('conversations.newConversation.title')}</DialogTitle>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Search Existing Lead */}
            <div className="space-y-3">
              <label className="text-sm font-medium text-foreground">
                {t('conversations.newConversation.searchLead')}
              </label>
              <AutocompleteInput
                value={searchQuery}
                options={leadOptions}
                selectedId={selectedLeadId}
                onChange={setSearchQuery}
                onSelect={handleLeadSelect}
                placeholder={t('conversations.newConversation.searchPlaceholder')}
                loading={loadingLeads}
                leftIcon={<Search className="h-4 w-4" />}
              />
              {selectedLead && (
                <div className="p-3 bg-primary/10 border border-primary/20 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-foreground">{selectedLead.name}</p>
                      <p className="text-sm text-muted-foreground">{selectedLead.phone}</p>
                      {selectedLead.email && (
                        <p className="text-sm text-muted-foreground">{selectedLead.email}</p>
                      )}
                    </div>
                    <button
                      onClick={() => setSelectedLead(null)}
                      className="text-muted-foreground hover:text-foreground"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Divider */}
            {!selectedLead && (
              <>
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <span className="w-full border-t border-border" />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-background px-2 text-muted-foreground">
                      {t('conversations.newConversation.orCreateNew')}
                    </span>
                  </div>
                </div>

                {/* Create New Lead */}
                <div className="space-y-3">
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-foreground">
                      {t('conversations.newConversation.name')} *
                    </label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        value={newLead.name}
                        onChange={(e) => setNewLead({ ...newLead, name: e.target.value })}
                        className="pl-10"
                        placeholder={t('conversations.newConversation.namePlaceholder')}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium text-foreground">
                      {t('conversations.newConversation.phone')} *
                    </label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        value={newLead.phone}
                        onChange={(e) => setNewLead({ ...newLead, phone: e.target.value })}
                        className="pl-10"
                        placeholder={t('conversations.newConversation.phonePlaceholder')}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium text-foreground">
                      {t('conversations.newConversation.email')}
                    </label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        type="email"
                        value={newLead.email}
                        onChange={(e) => setNewLead({ ...newLead, email: e.target.value })}
                        className="pl-10"
                        placeholder={t('conversations.newConversation.emailPlaceholder')}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium text-foreground">
                      {t('conversations.newConversation.source')}
                    </label>
                    <Select
                      value={newLead.source}
                      onValueChange={(value) => setNewLead({ ...newLead, source: value })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="WhatsApp">WhatsApp</SelectItem>
                        <SelectItem value="Instagram">Instagram</SelectItem>
                        <SelectItem value="Facebook">Facebook</SelectItem>
                        <SelectItem value="Website">Website</SelectItem>
                        <SelectItem value="Phone">Phone</SelectItem>
                        <SelectItem value="Other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <Button
                    onClick={handleCreateLead}
                    disabled={loading || !newLead.name.trim() || !newLead.phone.trim()}
                    className="w-full"
                    variant="outline"
                  >
                    {loading ? t('common.creating') : t('conversations.newConversation.createLead')}
                  </Button>
                </div>
              </>
            )}

            {selectedLead && (
              <div className="space-y-3">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-foreground">
                    {t('conversations.newConversation.selectTemplate')} *
                  </label>
                  <Select
                    value={selectedTemplate?.id || ''}
                    onValueChange={handleTemplateSelect}
                    disabled={loadingTemplates}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={loadingTemplates ? t('common.loading') : t('conversations.newConversation.selectTemplatePlaceholder')} />
                    </SelectTrigger>
                    <SelectContent>
                      {templates.map((template) => (
                        <SelectItem key={template.id} value={template.id}>
                          {template.name} ({template.language})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {selectedTemplate && (
                  <>
                    <div className="p-3 bg-muted/50 border border-border rounded-lg">
                      <p className="text-xs font-medium text-muted-foreground mb-1">
                        {t('conversations.newConversation.preview')}:
                      </p>
                      <p className="text-sm text-foreground whitespace-pre-wrap">
                        {selectedTemplate.components.find((c) => c.type === 'BODY')?.text || ''}
                      </p>
                    </div>

                    {extractTemplateParameters(selectedTemplate) > 0 && (
                      <div className="space-y-2">
                        <label className="text-sm font-medium text-foreground">
                          {t('conversations.newConversation.parameters')} *
                        </label>
                        {Object.keys(templateParameters).map((key) => {
                          const paramNum = parseInt(key);
                          return (
                            <Input
                              key={key}
                              value={templateParameters[paramNum]}
                              onChange={(e) =>
                                setTemplateParameters({
                                  ...templateParameters,
                                  [paramNum]: e.target.value,
                                })
                              }
                              placeholder={`${t('conversations.newConversation.parameter')} ${paramNum}`}
                            />
                          );
                        })}
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button onClick={handleClose} variant="outline" disabled={loading}>
              {t('common.cancel')}
            </Button>
            <Button
              onClick={handleStartConversation}
              disabled={loading || !selectedLead || !selectedTemplate}
            >
              {loading ? t('common.sending') : t('conversations.newConversation.sendAndStart')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </ShadcnDialog>
    </>
  );
};
